import { Text } from '@fly/core';
import { FlySvgSprite } from '@fly/svg';
import { Table } from '@radix-ui/themes';
import { Table as TanstackTable, flexRender } from '@tanstack/react-table';
import cn from 'classnames';

import { getColumnMeta } from '../utils/getColumnHtmlMeta';
import styles from './TanstackTable.module.less';

export interface TanstackTableHeaderProps<T extends Record<string, any>> {
  table: TanstackTable<T>;
  /** When false, sort click handlers and sort icons are omitted (e.g. loading state). Default true. */
  interactive?: boolean;
}

export function TanstackTableHeader<T extends Record<string, any>>({
  table,
  interactive = true,
}: TanstackTableHeaderProps<T>) {
  return (
    <Table.Header>
      {table.getHeaderGroups().map((headerGroup) => (
        <Table.Row key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const { htmlProps } = getColumnMeta(header.column.columnDef.meta);
            return (
              <Table.ColumnHeaderCell
                key={header.id}
                colSpan={header.colSpan}
                minWidth={
                  header.column.columnDef.minSize
                    ? `${header.column.columnDef.minSize}px`
                    : undefined
                }
                maxWidth={
                  header.column.columnDef.maxSize
                    ? `${header.column.columnDef.maxSize}px`
                    : undefined
                }
                width={`${header.column.getSize()}px`}
                onClick={
                  interactive && header.column.getCanSort()
                    ? () => header.column.toggleSorting()
                    : undefined
                }
                {...htmlProps}
                className={cn(
                  (htmlProps as React.HTMLAttributes<HTMLElement>)?.className,
                  {
                    'cursor-pointer select-none':
                      interactive && header.column.getCanSort(),
                  }
                )}
              >
                <div
                  className={cn('gap-2 flex h-full w-full items-center', {
                    'px-0': table.getAllColumns().length === 1,
                  })}
                >
                  <Text size='2' className='w-full'>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Text>
                  {interactive && header.column.getCanSort() && (
                    <FlySvgSprite
                      name='accordion_chevron'
                      className={cn(styles.flySortIcon, 'shrink-0', {
                        [styles.asc]: header.column.getIsSorted() === 'asc',
                        [styles.desc]: header.column.getIsSorted() === 'desc',
                      })}
                      size={16}
                    />
                  )}
                </div>
              </Table.ColumnHeaderCell>
            );
          })}
        </Table.Row>
      ))}
    </Table.Header>
  );
}
